import { GenAITransformTxDSLResponse } from 'js/components/recipeBuilder/GenAiTransformButton/GenAITransformContext.types';
import { GEN_AI_MESSAGE_TYPE, Message } from '../components/MessagesContainer';
import { GEN_AI_ENDPOINTS } from '../api/streamFetch';

const createTxDSLMessage = (chunk: string, handleError: (error: string) => void): Message => {
  try {
    const { status, data } = JSON.parse(chunk) as GenAITransformTxDSLResponse;

    if (status !== 'success') throw new Error('Invalid response');

    if (!data?.dsl_code && data?.clarification_question) {
      return {
        id: 0,
        text: data.clarification_question,
        type: GEN_AI_MESSAGE_TYPE.TEXT,
        createdAt: new Date().toISOString(),
      };
    }

    if (!Array.isArray(data?.dsl_code)) {
      throw new Error('Invalid format for DSL code');
    }

    return {
      id: 0,
      data: data.dsl_code,
      rationale: data.rationale,
      type: GEN_AI_MESSAGE_TYPE.DSL_CODE,
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    handleError(error.message);
    return null;
  }
};

const createCodeGenMessage = (chunk: string, handleError: (error: string) => void): Message => {
  try {
    if (chunk === undefined) throw new Error('Chunk is required');

    const { status, data } = JSON.parse(chunk);

    if (status !== 'success') throw new Error('Invalid response');
    if (!data?.code) throw new Error('No code found');

    const language = data?.output_language || 'python';

    return {
      id: 0,
      text: data.code,
      language,
      isCode: true,
      isUser: false,
      type: GEN_AI_MESSAGE_TYPE.CODE,
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    const errorMessage = `Error parsing response, please try again. ${error.message}`;
    handleError(errorMessage);
    return null;
  }
};

const createTransformMessage = (chunk: string, handleError: (error: string) => void): Message => {
  try {
    if (typeof chunk !== 'string') throw new Error('Invalid response');

    let text = chunk;
    const isCode = chunk.startsWith('```') && chunk.endsWith('```');
    // remove extra quotes only if the text is wrapped in them
    if (!isCode && chunk.startsWith('"') && chunk.endsWith('"')) {
      text = chunk.replace(/^"|"$/g, '');
    }

    return {
      id: 0,
      text,
      isCode,
      isUser: false,
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    const errorMessage = `Error parsing response, please try again. ${error.message}`;
    handleError(errorMessage);
    return null;
  }
};

const createMessage = ({ chunk, handleError, type }): Message => {
  switch (type) {
    case GEN_AI_ENDPOINTS.GENAI_TX_DSL:
      return createTxDSLMessage(chunk, handleError);
    case GEN_AI_ENDPOINTS.GENAI_CODEGEN:
      return createCodeGenMessage(chunk, handleError);
    case GEN_AI_ENDPOINTS.GENAI_TRANSFORM:
      return createTransformMessage(chunk, handleError);
    default:
      throw new Error(`Unknown endpoint type: ${type}`);
  }
};

export default createMessage;
